<template>
  <div class="feedback-widget">

    <!-- Floating trigger button -->
    <button
      class="feedback-trigger"
      :class="{ open: isOpen }"
      @click="toggle"
      title="Enviar feedback"
      aria-label="Enviar feedback"
    >
      <span v-if="!isOpen" class="trigger-icon">💬</span>
      <span v-else class="trigger-icon close-icon">✕</span>
    </button>

    <!-- Panel -->
    <transition name="panel-slide">
      <div v-if="isOpen" class="feedback-panel">

        <!-- Header -->
        <div class="panel-header">
          <h3 class="panel-title">¿Tenés algún comentario?</h3>
          <p class="panel-subtitle">Tu opinión nos ayuda a mejorar</p>
        </div>

        <!-- Success state -->
        <div v-if="submitted" class="panel-success">
          <div class="success-icon">✓</div>
          <p class="success-msg">¡Gracias por tu feedback!</p>
          <p class="success-sub">Lo tendremos en cuenta.</p>
          <button class="btn-new" @click="resetForm">Enviar otro</button>
        </div>

        <!-- Form -->
        <div v-else class="panel-form">

          <!-- Name / email (if not logged in) -->
          <template v-if="!currentUser">
            <div class="field-row">
              <div class="field-group">
                <label>Nombre</label>
                <input type="text" v-model="form.name" class="field-input"
                  placeholder="Tu nombre" maxlength="100" />
              </div>
              <div class="field-group">
                <label>Email</label>
                <input type="email" v-model="form.email" class="field-input"
                  placeholder="tu@email.com" maxlength="255" />
              </div>
            </div>
          </template>
          <div v-else class="logged-as">
            Enviando como <strong>{{ currentUser.first_name || currentUser.email }}</strong>
          </div>

          <!-- Category -->
          <div class="field-group">
            <label>Categoría</label>
            <div class="category-pills">
              <button
                v-for="cat in categories" :key="cat.value"
                type="button"
                class="cat-pill"
                :class="{ active: form.category === cat.value }"
                @click="form.category = cat.value"
              >{{ cat.label }}</button>
            </div>
          </div>

          <!-- Star rating -->
          <div class="field-group">
            <label>Puntuación</label>
            <div class="stars">
              <button
                v-for="n in 5" :key="n"
                type="button"
                class="star-btn"
                :class="{ active: n <= (hoverRating || form.rating) }"
                @mouseenter="hoverRating = n"
                @mouseleave="hoverRating = 0"
                @click="form.rating = n"
              >★</button>
            </div>
          </div>

          <!-- Message -->
          <div class="field-group">
            <label>Mensaje <span class="required">*</span></label>
            <textarea
              v-model="form.message"
              class="field-input field-textarea"
              placeholder="Contanos qué pensás..."
              rows="3"
              maxlength="1000"
            ></textarea>
            <span class="char-count">{{ form.message.length }}/1000</span>
          </div>

          <p v-if="error" class="field-error">{{ error }}</p>

          <button class="btn-submit" :disabled="loading" @click="submit">
            <span v-if="loading" class="spinner"></span>
            <span v-else>Enviar feedback</span>
          </button>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
import { authStore } from '@/store/auth.js'

export default {
  name: 'FeedbackWidget',
  data() {
    return {
      isOpen: false,
      submitted: false,
      loading: false,
      error: '',
      hoverRating: 0,
      form: {
        name: '',
        email: '',
        category: '',
        rating: 0,
        message: '',
      },
      categories: [
        { value: 'suggestion', label: '💡 Sugerencia' },
        { value: 'bug',        label: '🐛 Error' },
        { value: 'other',      label: '💬 Otro' },
      ],
    }
  },
  computed: {
    currentUser() {
      return authStore.isLoggedIn ? authStore.currentUser : null
    },
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen
    },
    resetForm() {
      this.submitted = false
      this.form = { name: '', email: '', category: '', rating: 0, message: '' }
      this.error = ''
    },
    async submit() {
      this.error = ''
      if (!this.form.message.trim()) {
        this.error = 'Por favor escribí un mensaje.'
        return
      }
      this.loading = true
      try {
        const headers = { 'Content-Type': 'application/json' }
        if (authStore.token) headers['Authorization'] = `Bearer ${authStore.token}`

        const res = await fetch('/api/feedback', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            name:      this.form.name,
            email:     this.form.email,
            category:  this.form.category || null,
            rating:    this.form.rating || null,
            message:   this.form.message,
            page_path: this.$route?.path || window.location.pathname,
          }),
        })
        if (res.ok) {
          this.submitted = true
        } else {
          const data = await res.json()
          this.error = data.error || 'Error al enviar. Intentá de nuevo.'
        }
      } catch {
        this.error = 'Error de conexión.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Trigger button ── */
.feedback-widget {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 1000;
}

.feedback-trigger {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #c9a227;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  transition: transform 0.2s, background 0.2s;
}
.feedback-trigger:hover { transform: scale(1.1); }
.feedback-trigger.open  { background: #444; }
.trigger-icon { font-size: 22px; line-height: 1; }
.close-icon   { font-size: 16px; color: #fff; }

/* ── Panel ── */
.feedback-panel {
  position: absolute;
  bottom: 64px;
  right: 0;
  width: 320px;
  background: #111;
  border: 1px solid #2a2a2a;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.6);
  overflow: hidden;
}

.panel-header {
  background: #000;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #1e1e1e;
}
.panel-title {
  font-size: 15px;
  font-weight: bold;
  color: #fff;
  margin: 0 0 4px;
}
.panel-subtitle {
  font-size: 12px;
  color: #666;
  margin: 0;
}

/* ── Form ── */
.panel-form {
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.logged-as {
  font-size: 12px;
  color: #666;
  background: #0a0a0a;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 2px solid #c9a227;
}
.logged-as strong { color: #c9a227; }

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
label {
  font-size: 11px;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.required { color: #e74c3c; }

.field-input {
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  padding: 8px 10px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  font-family: inherit;
}
.field-input:focus { outline: none; border-color: #c9a227; }
.field-textarea { resize: none; }
.char-count { font-size: 11px; color: #444; text-align: right; }

/* Category pills */
.category-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.cat-pill {
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 20px;
  color: #888;
  font-size: 12px;
  padding: 5px 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.cat-pill:hover  { border-color: #c9a227; color: #c9a227; }
.cat-pill.active { background: #c9a22720; border-color: #c9a227; color: #c9a227; font-weight: bold; }

/* Stars */
.stars { display: flex; gap: 4px; }
.star-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.1s, transform 0.1s;
}
.star-btn:hover,
.star-btn.active { color: #c9a227; transform: scale(1.15); }

/* Submit */
.btn-submit {
  background: #c9a227;
  color: #000;
  font-weight: bold;
  font-size: 13px;
  padding: 11px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s;
  margin-top: 2px;
}
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-submit:hover:not(:disabled) { opacity: 0.85; }

.field-error {
  font-size: 12px;
  color: #e74c3c;
  background: rgba(231,76,60,0.08);
  padding: 7px 10px;
  border-radius: 5px;
  margin: 0;
}

/* Success */
.panel-success {
  padding: 32px 20px;
  text-align: center;
}
.success-icon {
  width: 48px;
  height: 48px;
  background: #c9a22720;
  border: 2px solid #c9a227;
  border-radius: 50%;
  color: #c9a227;
  font-size: 22px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}
.success-msg { color: #fff; font-size: 16px; font-weight: bold; margin: 0 0 6px; }
.success-sub { color: #666; font-size: 13px; margin: 0 0 20px; }
.btn-new {
  background: none;
  border: 1px solid #333;
  color: #888;
  font-size: 13px;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-new:hover { border-color: #c9a227; color: #c9a227; }

/* Spinner */
.spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(0,0,0,0.3);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Transition */
.panel-slide-enter-active { animation: slideUp 0.22s ease-out; }
.panel-slide-leave-active { animation: slideUp 0.18s ease-in reverse; }
@keyframes slideUp {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}

/* Mobile */
@media (max-width: 480px) {
  .feedback-widget { bottom: 20px; right: 16px; }
  .feedback-panel  { width: calc(100vw - 32px); right: 0; }
}
</style>
